import { Button } from '@affine/component';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import {
  SettingsContentSurface,
  SettingsContentText,
} from '../shared/settings-content';
import {
  SettingsBadge,
  SettingsEntityList,
  SettingsEntityMessage,
  SettingsExpandableItem,
  SettingsMetaItem,
} from '../shared/settings-entity';
import { SettingsEntityPage } from '../shared/settings-entity-page';
import { requestWorkspaceSettingGraphQL } from '../shared/workspace-settings-api';

const PAGE_SIZE = 100;
const GRAPH_LIST_MAX_HEIGHT = 560;
const GRAPH_LIST_ESTIMATED_ROW_HEIGHT = 76;

type PermissionGraphMode =
  | 'roots'
  | 'folder-subtree'
  | 'relationships'
  | 'resource-subjects'
  | 'subject-resources'
  | 'changes';

interface PermissionGraphResourceNode {
  __typename: 'PermissionGraphNodeType';
  id: string;
  kind: string;
  label: string;
  workspaceId: string;
  metadata: Record<string, unknown> | null;
}

interface PermissionGraphRelationship {
  __typename: 'PermissionGraphRelationshipType';
  id: string;
  relationshipType: string;
  sourceType: string;
  sourceId: string;
  targetType: string;
  targetId: string;
  role: string | null;
  provenance: {
    sourceType: string;
    sourceId: string;
    viaGroupId: string | null;
    viaFolderId: string | null;
    inherited: boolean;
    effective: boolean;
  };
  updatedAt: string | null;
}

type PermissionGraphElement =
  | PermissionGraphRelationship
  | PermissionGraphResourceNode;

interface PermissionGraphEdge {
  node: PermissionGraphElement;
  cursor: string;
  sortKey: string;
}

interface PermissionGraphConnection {
  edges: PermissionGraphEdge[];
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
    token?: string | null;
    resetRequired?: string | null;
  };
  permissionGraphVersion: number;
  diagnostics: {
    bounded: boolean;
    maxPageSize: number;
    maxSubtreeDepth: number;
    projectionStatus: string;
    resetReason: string | null;
  };
}

interface PermissionGraphResponse {
  permissionGraphRoots?: PermissionGraphConnection;
  permissionFolderSubtree?: PermissionGraphConnection;
  permissionGraphRelationships?: PermissionGraphConnection;
  permissionResourceSubjects?: PermissionGraphConnection;
  permissionSubjectResources?: PermissionGraphConnection;
  permissionGraphChanges?: PermissionGraphConnection;
}

interface PermissionGraphDriftFinding {
  kind: string;
  docId: string | null;
  userId: string | null;
  sourceId: string | null;
}

interface PermissionGraphDriftDiagnostics {
  permissionGraphVersion: number;
  sampledDocs: number;
  sampledPrincipals: number;
  truncated: boolean;
  findings: PermissionGraphDriftFinding[];
}

interface PermissionGraphFilterState {
  subjectId: string;
  subjectKind: string;
  resourceId: string;
  resourceKind: string;
  permission: string;
  sinceToken: string;
  subtreeDepth: number;
}

const PERMISSION_GRAPH_QUERY = /* GraphQL */ `
  query WorkspacePermissionGraph(
    $workspaceId: String!
    $isRoots: Boolean!
    $isFolderSubtree: Boolean!
    $isRelationships: Boolean!
    $isResourceSubjects: Boolean!
    $isSubjectResources: Boolean!
    $isChanges: Boolean!
    $filter: PermissionGraphFilterInput
    $folderId: String!
    $depth: Int!
    $subjectId: String!
    $subjectKind: String!
    $resourceId: String!
    $resourceKind: String!
    $permission: String
    $sinceToken: String
    $first: Int
    $after: String
  ) {
    permissionGraphRoots(
      workspaceId: $workspaceId
      first: $first
      after: $after
    ) @include(if: $isRoots) {
      ...PermissionGraphConnectionFields
    }
    permissionFolderSubtree(
      workspaceId: $workspaceId
      folderId: $folderId
      depth: $depth
      permission: $permission
      first: $first
      after: $after
    ) @include(if: $isFolderSubtree) {
      ...PermissionGraphConnectionFields
    }
    permissionGraphRelationships(
      workspaceId: $workspaceId
      filter: $filter
      first: $first
      after: $after
    ) @include(if: $isRelationships) {
      ...PermissionGraphConnectionFields
    }
    permissionResourceSubjects(
      workspaceId: $workspaceId
      resourceId: $resourceId
      resourceKind: $resourceKind
      permission: $permission
      first: $first
      after: $after
    ) @include(if: $isResourceSubjects) {
      ...PermissionGraphConnectionFields
    }
    permissionSubjectResources(
      workspaceId: $workspaceId
      subjectId: $subjectId
      subjectKind: $subjectKind
      resourceKind: $resourceKind
      permission: $permission
      first: $first
      after: $after
    ) @include(if: $isSubjectResources) {
      ...PermissionGraphConnectionFields
    }
    permissionGraphChanges(
      workspaceId: $workspaceId
      sinceToken: $sinceToken
      first: $first
      after: $after
    ) @include(if: $isChanges) {
      ...PermissionGraphConnectionFields
    }
  }

  fragment PermissionGraphConnectionFields on PermissionGraphConnectionType {
    permissionGraphVersion
    edges {
      cursor
      sortKey
      node {
        __typename
        ... on PermissionGraphNodeType {
          id
          kind
          label
          workspaceId
          metadata
        }
        ... on PermissionGraphRelationshipType {
          id
          relationshipType
          sourceType
          sourceId
          targetType
          targetId
          role
          provenance {
            sourceType
            sourceId
            viaGroupId
            viaFolderId
            inherited
            effective
          }
          updatedAt
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
      token
      resetRequired
    }
    diagnostics {
      bounded
      maxPageSize
      maxSubtreeDepth
      projectionStatus
      resetReason
    }
  }
`;

const PERMISSION_GRAPH_DIAGNOSTICS_QUERY = /* GraphQL */ `
  query WorkspacePermissionGraphDiagnostics(
    $workspaceId: String!
    $sampleDocs: Int!
  ) {
    permissionGraphProjectionDiagnostics(
      workspaceId: $workspaceId
      sampleDocs: $sampleDocs
    ) {
      permissionGraphVersion
      sampledDocs
      sampledPrincipals
      truncated
      findings {
        kind
        docId
        userId
        sourceId
      }
    }
  }
`;

const fieldStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 180,
} as const;

const labelStyle = {
  color: 'var(--streamlain-v2-text-secondary)',
  fontSize: 12,
  fontWeight: 600,
} as const;

const inputStyle = {
  minHeight: 34,
  borderRadius: 6,
  border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  background: 'var(--streamlain-v2-layer-background-primary)',
  color: 'var(--streamlain-v2-text-primary)',
  padding: '6px 10px',
  font: 'inherit',
} as const;

const controlsStyle = {
  display: 'flex',
  alignItems: 'end',
  flexWrap: 'wrap',
  gap: 12,
  marginBottom: 16,
} as const;

const idStyle = {
  fontFamily: 'var(--affine-font-monospace-family)',
  fontSize: 12,
  wordBreak: 'break-all',
} as const;

export const WorkspaceSettingPermissionGraph = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const workspaceId = useService(WorkspaceService).workspace.id;
  const [mode, setMode] = useState<PermissionGraphMode>('roots');
  const [filterDraft, setFilterDraft] = useState<PermissionGraphFilterState>({
    subjectId: '',
    subjectKind: 'user',
    resourceId: '',
    resourceKind: 'doc',
    permission: '',
    sinceToken: '',
    subtreeDepth: 1,
  });
  const [filter, setFilter] = useState<PermissionGraphFilterState>(filterDraft);
  const [edges, setEdges] = useState<PermissionGraphEdge[]>([]);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [permissionGraphVersion, setPermissionGraphVersion] = useState<
    number | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const changeTokenRef = useRef<string | null>(null);
  const [resetReason, setResetReason] = useState<string | null>(null);
  const [projectionStatus, setProjectionStatus] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] =
    useState<PermissionGraphDriftDiagnostics | null>(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [diagnosticsError, setDiagnosticsError] = useState<string | null>(null);
  const requestGenerationRef = useRef(0);
  const requestAbortRef = useRef<AbortController | null>(null);

  const graphFilter = useMemo(() => {
    const next: Record<string, string> = {};
    if (filter.subjectId.trim()) {
      next.subjectId = filter.subjectId.trim();
      next.subjectType = filter.subjectKind;
    }
    if (filter.resourceId.trim()) {
      next.resourceId = filter.resourceId.trim();
      next.resourceType = filter.resourceKind;
    }
    if (filter.permission.trim()) {
      next.permission = filter.permission.trim();
    }
    return Object.keys(next).length ? next : undefined;
  }, [filter]);

  const loadPage = useCallback(
    async (cursor?: string | null, append = false) => {
      const requestGeneration = ++requestGenerationRef.current;
      requestAbortRef.current?.abort();
      if (!backendAvailable) {
        setLoading(false);
        setError(null);
        return;
      }
      if (!hasRequiredGraphInput(mode, filter)) {
        setEdges([]);
        setEndCursor(null);
        setHasNextPage(false);
        setLoading(false);
        setError(null);
        return;
      }
      const abortController = new AbortController();
      requestAbortRef.current = abortController;
      setLoading(true);
      setError(null);
      try {
        const isRoots = mode === 'roots';
        const isFolderSubtree = mode === 'folder-subtree';
        const isRelationships = mode === 'relationships';
        const isResourceSubjects = mode === 'resource-subjects';
        const isSubjectResources = mode === 'subject-resources';
        const isChanges = mode === 'changes';
        const response = await requestWorkspaceSettingGraphQL(
          workspaceId,
          PERMISSION_GRAPH_QUERY,
          {
            workspaceId,
            isRoots,
            isFolderSubtree,
            isRelationships,
            isResourceSubjects,
            isSubjectResources,
            isChanges,
            filter: isRelationships ? graphFilter : undefined,
            folderId: filter.resourceId.trim(),
            depth: filter.subtreeDepth,
            subjectId: filter.subjectId.trim(),
            subjectKind: filter.subjectKind,
            resourceId: filter.resourceId.trim(),
            resourceKind: filter.resourceKind,
            permission: filter.permission.trim() || undefined,
            sinceToken:
              filter.sinceToken.trim() || changeTokenRef.current || undefined,
            first: PAGE_SIZE,
            after: cursor ?? undefined,
          },
          'background',
          'permission-graph',
          abortController.signal
        );
        const json = (await response.json()) as {
          data?: PermissionGraphResponse;
          errors?: Array<{
            message?: string;
            extensions?: { code?: string; resetRequired?: string };
          }>;
        };
        if (json.errors?.length) {
          const graphError = json.errors[0];
          if (graphError?.extensions?.code === 'GRAPH_QUERY_LIMIT_EXCEEDED') {
            throw new Error(
              t['com.streamlain.settings.workspace.permission-graph.limit']()
            );
          }
          if (
            graphError?.extensions?.code === 'PERMISSION_GRAPH_CURSOR_INVALID'
          ) {
            throw new Error(
              t[
                'com.streamlain.settings.workspace.permission-graph.cursor-invalid'
              ]()
            );
          }
          throw new Error(
            t['com.streamlain.settings.workspace.permission-graph.error']()
          );
        }
        const page = pageForMode(json.data, mode);
        if (!page) {
          throw new Error(
            t[
              'com.streamlain.settings.workspace.permission-graph.empty-response'
            ]()
          );
        }
        if (requestGeneration !== requestGenerationRef.current) return;
        setPermissionGraphVersion(page.permissionGraphVersion);
        setProjectionStatus(page.diagnostics.projectionStatus);
        changeTokenRef.current = page.pageInfo.token ?? changeTokenRef.current;
        setResetReason(
          page.pageInfo.resetRequired ?? page.diagnostics.resetReason ?? null
        );
        setEdges(prev => (append ? [...prev, ...page.edges] : page.edges));
        setEndCursor(page.pageInfo.endCursor);
        setHasNextPage(page.pageInfo.hasNextPage);
      } catch (err) {
        if (
          abortController.signal.aborted ||
          requestGeneration !== requestGenerationRef.current
        ) {
          return;
        }
        setError(
          err instanceof Error
            ? err.message
            : t['com.streamlain.settings.workspace.permission-graph.error']()
        );
        if (!append) {
          setEdges([]);
          setEndCursor(null);
          setHasNextPage(false);
        }
      } finally {
        if (requestGeneration === requestGenerationRef.current) {
          setLoading(false);
          requestAbortRef.current = null;
        }
      }
    },
    [backendAvailable, filter, graphFilter, mode, t, workspaceId]
  );

  useEffect(() => {
    setEdges([]);
    setEndCursor(null);
    setHasNextPage(false);
    setPermissionGraphVersion(null);
    setError(null);
    setExpanded(null);
    setResetReason(null);
    setProjectionStatus(null);
    void loadPage(null, false);
    return () => {
      requestGenerationRef.current += 1;
      requestAbortRef.current?.abort();
      requestAbortRef.current = null;
    };
  }, [loadPage]);

  const applyFilter = useCallback(() => {
    changeTokenRef.current = null;
    setFilter(filterDraft);
    setEndCursor(null);
  }, [filterDraft]);

  const resetFilter = useCallback(() => {
    changeTokenRef.current = null;
    const empty = {
      subjectId: '',
      subjectKind: 'user',
      resourceId: '',
      resourceKind: 'doc',
      permission: '',
      sinceToken: '',
      subtreeDepth: 1,
    };
    setFilterDraft(empty);
    setFilter(empty);
    setEndCursor(null);
  }, []);

  const runProjectionDiagnostics = useCallback(async () => {
    if (!backendAvailable || diagnosticsLoading) return;
    setDiagnosticsLoading(true);
    setDiagnosticsError(null);
    try {
      const response = await requestWorkspaceSettingGraphQL(
        workspaceId,
        PERMISSION_GRAPH_DIAGNOSTICS_QUERY,
        { workspaceId, sampleDocs: 10 },
        'background',
        'permission-graph'
      );
      const json = (await response.json()) as {
        data?: {
          permissionGraphProjectionDiagnostics?: PermissionGraphDriftDiagnostics;
        };
        errors?: unknown[];
      };
      const result = json.data?.permissionGraphProjectionDiagnostics;
      if (json.errors?.length || !result) {
        throw new Error(
          t[
            'com.streamlain.settings.workspace.permission-graph.diagnostics-error'
          ]()
        );
      }
      setDiagnostics(result);
    } catch {
      setDiagnostics(null);
      setDiagnosticsError(
        t[
          'com.streamlain.settings.workspace.permission-graph.diagnostics-error'
        ]()
      );
    } finally {
      setDiagnosticsLoading(false);
    }
  }, [backendAvailable, diagnosticsLoading, t, workspaceId]);

  const title = t['com.streamlain.settings.workspace.permission-graph.title']();
  const subtitle =
    t['com.streamlain.settings.workspace.permission-graph.subtitle']();

  return (
    <SettingsEntityPage
      title={title}
      subtitle={subtitle}
      headerAction={<SettingsOfflineHeaderAction show={!backendAvailable} />}
    >
      <SettingsContentSurface>
        <div style={controlsStyle}>
          <label style={fieldStyle}>
            <span style={labelStyle}>
              {t['com.streamlain.settings.workspace.permission-graph.view']()}
            </span>
            <select
              value={mode}
              onChange={event => {
                changeTokenRef.current = null;
                setResetReason(null);
                setMode(event.currentTarget.value as PermissionGraphMode);
              }}
              style={inputStyle}
            >
              <option value="roots">
                {t[
                  'com.streamlain.settings.workspace.permission-graph.roots'
                ]()}
              </option>
              <option value="relationships">
                {t[
                  'com.streamlain.settings.workspace.permission-graph.relationships'
                ]()}
              </option>
              <option value="folder-subtree">
                {t[
                  'com.streamlain.settings.workspace.permission-graph.folder-subtree'
                ]()}
              </option>
              <option value="resource-subjects">
                {t[
                  'com.streamlain.settings.workspace.permission-graph.resource-subjects'
                ]()}
              </option>
              <option value="subject-resources">
                {t[
                  'com.streamlain.settings.workspace.permission-graph.subject-resources'
                ]()}
              </option>
              <option value="changes">
                {t[
                  'com.streamlain.settings.workspace.permission-graph.changes'
                ]()}
              </option>
            </select>
          </label>
          {mode === 'relationships' || mode === 'subject-resources' ? (
            <>
              <GraphFilterSelect
                label={t[
                  'com.streamlain.settings.workspace.permission-graph.subject-type'
                ]()}
                value={filterDraft.subjectKind}
                options={[
                  {
                    value: 'user',
                    label:
                      t[
                        'com.streamlain.settings.workspace.permission-graph.kind.user'
                      ](),
                  },
                  {
                    value: 'group',
                    label:
                      t[
                        'com.streamlain.settings.workspace.permission-graph.kind.group'
                      ](),
                  },
                ]}
                onChange={subjectKind =>
                  setFilterDraft(prev => ({ ...prev, subjectKind }))
                }
              />
              <GraphFilterInput
                label={t[
                  'com.streamlain.settings.workspace.permission-graph.subject'
                ]()}
                value={filterDraft.subjectId}
                onChange={subjectId =>
                  setFilterDraft(prev => ({ ...prev, subjectId }))
                }
              />
            </>
          ) : null}
          {mode === 'relationships' || mode === 'resource-subjects' ? (
            <GraphFilterSelect
              label={t[
                'com.streamlain.settings.workspace.permission-graph.resource-type'
              ]()}
              value={filterDraft.resourceKind}
              options={[
                {
                  value: 'doc',
                  label:
                    t[
                      'com.streamlain.settings.workspace.permission-graph.kind.doc'
                    ](),
                },
                {
                  value: 'folder',
                  label:
                    t[
                      'com.streamlain.settings.workspace.permission-graph.kind.folder'
                    ](),
                },
              ]}
              onChange={resourceKind =>
                setFilterDraft(prev => ({ ...prev, resourceKind }))
              }
            />
          ) : null}
          {mode === 'relationships' ||
          mode === 'resource-subjects' ||
          mode === 'folder-subtree' ? (
            <GraphFilterInput
              label={t[
                'com.streamlain.settings.workspace.permission-graph.resource'
              ]()}
              value={filterDraft.resourceId}
              onChange={resourceId =>
                setFilterDraft(prev => ({ ...prev, resourceId }))
              }
            />
          ) : null}
          {mode === 'folder-subtree' ? (
            <label style={fieldStyle}>
              <span style={labelStyle}>
                {t[
                  'com.streamlain.settings.workspace.permission-graph.depth'
                ]()}
              </span>
              <select
                value={filterDraft.subtreeDepth}
                onChange={event =>
                  setFilterDraft(prev => ({
                    ...prev,
                    subtreeDepth: Number(event.currentTarget.value),
                  }))
                }
                style={inputStyle}
              >
                {[1, 2, 3, 4].map(depth => (
                  <option key={depth} value={depth}>
                    {depth}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {mode !== 'roots' && mode !== 'changes' ? (
            <GraphFilterInput
              label={t[
                'com.streamlain.settings.workspace.permission-graph.permission'
              ]()}
              value={filterDraft.permission}
              onChange={permission =>
                setFilterDraft(prev => ({ ...prev, permission }))
              }
            />
          ) : null}
          {mode === 'changes' ? (
            <GraphFilterInput
              label={t[
                'com.streamlain.settings.workspace.permission-graph.since-token'
              ]()}
              value={filterDraft.sinceToken}
              onChange={sinceToken =>
                setFilterDraft(prev => ({ ...prev, sinceToken }))
              }
            />
          ) : null}
          <Button
            variant="secondary"
            disabled={
              mode === 'roots' ||
              loading ||
              !hasRequiredGraphInput(mode, filterDraft)
            }
            onClick={applyFilter}
          >
            {t['com.streamlain.settings.workspace.permission-graph.apply']()}
          </Button>
          <Button variant="secondary" disabled={loading} onClick={resetFilter}>
            {t['com.streamlain.settings.workspace.permission-graph.reset']()}
          </Button>
        </div>
        <SettingsContentText>
          {permissionGraphVersion !== null
            ? `${t[
                'com.streamlain.settings.workspace.permission-graph.version'
              ]()}: ${permissionGraphVersion}`
            : offlineLabel}
        </SettingsContentText>
        {resetReason ? (
          <SettingsContentText>
            {t[
              'com.streamlain.settings.workspace.permission-graph.reset-required'
            ]()}
          </SettingsContentText>
        ) : null}
        {projectionStatus ? (
          <SettingsContentText>
            {t[
              'com.streamlain.settings.workspace.permission-graph.projection-status'
            ]()}
          </SettingsContentText>
        ) : null}
        <div style={{ marginTop: 12 }}>
          <Button
            variant="secondary"
            disabled={!backendAvailable || diagnosticsLoading}
            onClick={() => void runProjectionDiagnostics()}
          >
            {diagnosticsLoading
              ? t[
                  'com.streamlain.settings.workspace.permission-graph.diagnostics-checking'
                ]()
              : t[
                  'com.streamlain.settings.workspace.permission-graph.diagnostics-check'
                ]()}
          </Button>
        </div>
        {diagnosticsError ? (
          <SettingsContentText>{diagnosticsError}</SettingsContentText>
        ) : diagnostics ? (
          <ProjectionDiagnosticsSummary diagnostics={diagnostics} t={t} />
        ) : null}
      </SettingsContentSurface>

      {error ? <SettingsEntityMessage>{error}</SettingsEntityMessage> : null}
      {!backendAvailable ? (
        <SettingsEntityMessage>{offlineLabel}</SettingsEntityMessage>
      ) : loading && edges.length === 0 ? (
        <SettingsEntityMessage>
          {t['com.streamlain.settings.workspace.permission-graph.loading']()}
        </SettingsEntityMessage>
      ) : edges.length === 0 ? (
        <SettingsEntityMessage>
          {t['com.streamlain.settings.workspace.permission-graph.empty']()}
        </SettingsEntityMessage>
      ) : (
        <Virtuoso
          data={edges}
          style={{
            height: Math.min(
              GRAPH_LIST_MAX_HEIGHT,
              edges.length * GRAPH_LIST_ESTIMATED_ROW_HEIGHT
            ),
          }}
          itemContent={(_, edge) => (
            <SettingsEntityList>
              <SettingsExpandableItem
                title={formatEdgeTitle(edge.node, t)}
                expanded={expanded === edge.cursor}
                onToggle={() =>
                  setExpanded(prev =>
                    prev === edge.cursor ? null : edge.cursor
                  )
                }
                badges={
                  <>
                    <SettingsBadge>{elementLabel(edge.node, t)}</SettingsBadge>
                    {isRelationship(edge.node) && edge.node.role ? (
                      <SettingsBadge variant="active">
                        {roleLabel(edge.node.role, t)}
                      </SettingsBadge>
                    ) : null}
                  </>
                }
                meta={
                  <>
                    <SettingsMetaItem>
                      {elementMeta(edge.node, t)}
                    </SettingsMetaItem>
                    {isRelationship(edge.node) && edge.node.updatedAt ? (
                      <SettingsMetaItem>
                        {new Date(edge.node.updatedAt).toLocaleString()}
                      </SettingsMetaItem>
                    ) : null}
                  </>
                }
              >
                <PermissionGraphDetails node={edge.node} t={t} />
              </SettingsExpandableItem>
            </SettingsEntityList>
          )}
        />
      )}
      {hasNextPage ? (
        <div style={{ marginTop: 16 }}>
          <Button
            variant="secondary"
            disabled={loading}
            onClick={() => void loadPage(endCursor, true)}
          >
            {loading
              ? t[
                  'com.streamlain.settings.workspace.permission-graph.loading'
                ]()
              : t[
                  'com.streamlain.settings.workspace.permission-graph.load-more'
                ]()}
          </Button>
        </div>
      ) : null}
    </SettingsEntityPage>
  );
};

function GraphFilterInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label style={fieldStyle}>
      <span style={labelStyle}>{label}</span>
      <input
        value={value}
        onChange={event => onChange(event.currentTarget.value)}
        style={inputStyle}
      />
    </label>
  );
}

function GraphFilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <label style={fieldStyle}>
      <span style={labelStyle}>{label}</span>
      <select
        value={value}
        onChange={event => onChange(event.currentTarget.value)}
        style={inputStyle}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function hasRequiredGraphInput(
  mode: PermissionGraphMode,
  filter: PermissionGraphFilterState
): boolean {
  switch (mode) {
    case 'folder-subtree':
    case 'resource-subjects':
      return filter.resourceId.trim().length > 0;
    case 'subject-resources':
      return filter.subjectId.trim().length > 0;
    default:
      return true;
  }
}

function pageForMode(
  response: PermissionGraphResponse | undefined,
  mode: PermissionGraphMode
): PermissionGraphConnection | undefined {
  switch (mode) {
    case 'roots':
      return response?.permissionGraphRoots;
    case 'folder-subtree':
      return response?.permissionFolderSubtree;
    case 'relationships':
      return response?.permissionGraphRelationships;
    case 'resource-subjects':
      return response?.permissionResourceSubjects;
    case 'subject-resources':
      return response?.permissionSubjectResources;
    case 'changes':
      return response?.permissionGraphChanges;
  }
}

type PermissionGraphI18n = ReturnType<typeof useI18n>;

function formatEdgeTitle(
  node: PermissionGraphElement,
  t: PermissionGraphI18n
): string {
  if (!isRelationship(node)) return node.label;
  return `${kindLabel(node.sourceType, t)} ${shortId(node.sourceId)} -> ${kindLabel(node.targetType, t)} ${shortId(node.targetId)}`;
}

function PermissionGraphDetails({
  node,
  t,
}: {
  node: PermissionGraphElement;
  t: PermissionGraphI18n;
}) {
  if (!isRelationship(node)) {
    return (
      <div style={{ display: 'grid', gap: 8 }}>
        <DetailRow label="ID" value={node.id} />
        <DetailRow
          label={t[
            'com.streamlain.settings.workspace.permission-graph.target'
          ]()}
          value={kindLabel(node.kind, t)}
        />
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <DetailRow
        label={t['com.streamlain.settings.workspace.permission-graph.source']()}
        value={`${kindLabel(node.sourceType, t)}: ${node.sourceId}`}
      />
      <DetailRow
        label={t['com.streamlain.settings.workspace.permission-graph.target']()}
        value={`${kindLabel(node.targetType, t)}: ${node.targetId}`}
      />
      <DetailRow
        label={t[
          'com.streamlain.settings.workspace.permission-graph.relationship'
        ]()}
        value={relationshipLabel(node.relationshipType, t)}
      />
      {node.role ? (
        <DetailRow
          label={t['com.streamlain.settings.workspace.permission-graph.role']()}
          value={roleLabel(node.role, t)}
        />
      ) : null}
      {node.provenance ? (
        <DetailRow
          label={t[
            'com.streamlain.settings.workspace.permission-graph.provenance'
          ]()}
          value={`${relationshipLabel(node.provenance.sourceType, t)}: ${node.provenance.sourceId} · ${
            node.provenance.inherited
              ? t[
                  'com.streamlain.settings.workspace.permission-graph.inherited'
                ]()
              : t['com.streamlain.settings.workspace.permission-graph.direct']()
          }${
            node.provenance.effective
              ? ` · ${t[
                  'com.streamlain.settings.workspace.permission-graph.effective'
                ]()}`
              : ''
          }`}
        />
      ) : null}
    </div>
  );
}

function isRelationship(
  element: PermissionGraphElement
): element is PermissionGraphRelationship {
  return element.__typename === 'PermissionGraphRelationshipType';
}

function elementLabel(
  element: PermissionGraphElement,
  t: PermissionGraphI18n
): string {
  return isRelationship(element)
    ? relationshipLabel(element.relationshipType, t)
    : kindLabel(element.kind, t);
}

function elementMeta(
  element: PermissionGraphElement,
  t: PermissionGraphI18n
): string {
  return isRelationship(element)
    ? `${kindLabel(element.sourceType, t)} -> ${kindLabel(element.targetType, t)}`
    : kindLabel(element.kind, t);
}

function relationshipLabel(
  relationship: string,
  t: PermissionGraphI18n
): string {
  const labels: Record<string, string> = {
    workspace_root_folder:
      t[
        'com.streamlain.settings.workspace.permission-graph.relationship.workspace-root'
      ](),
    workspace_member:
      t[
        'com.streamlain.settings.workspace.permission-graph.relationship.workspace-member'
      ](),
    folder_parent:
      t[
        'com.streamlain.settings.workspace.permission-graph.relationship.folder-parent'
      ](),
    folder_contains_doc:
      t[
        'com.streamlain.settings.workspace.permission-graph.relationship.folder-doc'
      ](),
    folder_user_role:
      t[
        'com.streamlain.settings.workspace.permission-graph.relationship.folder-user'
      ](),
    folder_group_role:
      t[
        'com.streamlain.settings.workspace.permission-graph.relationship.folder-group'
      ](),
    doc_user_role:
      t[
        'com.streamlain.settings.workspace.permission-graph.relationship.doc-user'
      ](),
    doc_group_role:
      t[
        'com.streamlain.settings.workspace.permission-graph.relationship.doc-group'
      ](),
    effective_permission:
      t[
        'com.streamlain.settings.workspace.permission-graph.relationship.effective'
      ](),
    group_member:
      t[
        'com.streamlain.settings.workspace.permission-graph.relationship.group-member'
      ](),
    user_group_member:
      t[
        'com.streamlain.settings.workspace.permission-graph.relationship.group-member'
      ](),
    folder_doc:
      t[
        'com.streamlain.settings.workspace.permission-graph.relationship.folder-doc'
      ](),
    folder:
      t['com.streamlain.settings.workspace.permission-graph.kind.folder'](),
    direct: t['com.streamlain.settings.workspace.permission-graph.direct'](),
  };
  return (
    labels[relationship] ??
    t['com.streamlain.settings.workspace.permission-graph.relationship.other']()
  );
}

function kindLabel(kind: string, t: PermissionGraphI18n): string {
  const labels: Record<string, string> = {
    user: t['com.streamlain.settings.workspace.permission-graph.kind.user'](),
    group: t['com.streamlain.settings.workspace.permission-graph.kind.group'](),
    workspace:
      t['com.streamlain.settings.workspace.permission-graph.kind.workspace'](),
    folder:
      t['com.streamlain.settings.workspace.permission-graph.kind.folder'](),
    doc: t['com.streamlain.settings.workspace.permission-graph.kind.doc'](),
  };
  return (
    labels[kind] ??
    t['com.streamlain.settings.workspace.permission-graph.kind.other']()
  );
}

function roleLabel(role: string, t: PermissionGraphI18n): string {
  const labels: Record<string, string> = {
    Owner: t['com.streamlain.settings.workspace.permission-graph.role.owner'](),
    Admin: t['com.streamlain.settings.workspace.permission-graph.role.admin'](),
    Manager:
      t['com.streamlain.settings.workspace.permission-graph.role.manager'](),
    Editor:
      t['com.streamlain.settings.workspace.permission-graph.role.editor'](),
    Commenter:
      t['com.streamlain.settings.workspace.permission-graph.role.commenter'](),
    Reader:
      t['com.streamlain.settings.workspace.permission-graph.role.reader'](),
    None: t['com.streamlain.settings.workspace.permission-graph.role.none'](),
  };
  return labels[role] ?? role;
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <div style={idStyle}>{value}</div>
    </div>
  );
}

function ProjectionDiagnosticsSummary({
  diagnostics,
  t,
}: {
  diagnostics: PermissionGraphDriftDiagnostics;
  t: PermissionGraphI18n;
}) {
  const summary = diagnostics.findings.length
    ? t['com.streamlain.settings.workspace.permission-graph.diagnostics-drift'](
        { count: String(diagnostics.findings.length) }
      )
    : t[
        'com.streamlain.settings.workspace.permission-graph.diagnostics-healthy'
      ]();
  return (
    <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
      <SettingsContentText>{summary}</SettingsContentText>
      <SettingsContentText>
        {t[
          'com.streamlain.settings.workspace.permission-graph.diagnostics-sample'
        ]({
          docs: String(diagnostics.sampledDocs),
          principals: String(diagnostics.sampledPrincipals),
        })}
      </SettingsContentText>
      {diagnostics.truncated ? (
        <SettingsContentText>
          {t[
            'com.streamlain.settings.workspace.permission-graph.diagnostics-truncated'
          ]()}
        </SettingsContentText>
      ) : null}
      {diagnostics.findings.map((finding, index) => (
        <SettingsContentText
          key={`${finding.kind}:${finding.docId ?? ''}:${finding.userId ?? ''}:${index}`}
        >
          {diagnosticFindingLabel(finding.kind, t)}
          {finding.docId ? ` · ${shortId(finding.docId)}` : ''}
        </SettingsContentText>
      ))}
    </div>
  );
}

function diagnosticFindingLabel(kind: string, t: PermissionGraphI18n): string {
  const labels: Record<string, string> = {
    projection_lag:
      t[
        'com.streamlain.settings.workspace.permission-graph.diagnostics-kind.lag'
      ](),
    version_gap:
      t[
        'com.streamlain.settings.workspace.permission-graph.diagnostics-kind.version'
      ](),
    missing_edge:
      t[
        'com.streamlain.settings.workspace.permission-graph.diagnostics-kind.missing'
      ](),
    stale_edge:
      t[
        'com.streamlain.settings.workspace.permission-graph.diagnostics-kind.stale'
      ](),
    tenant_mismatch:
      t[
        'com.streamlain.settings.workspace.permission-graph.diagnostics-kind.scope'
      ](),
    provenance_mismatch:
      t[
        'com.streamlain.settings.workspace.permission-graph.diagnostics-kind.provenance'
      ](),
  };
  return (
    labels[kind] ??
    t[
      'com.streamlain.settings.workspace.permission-graph.diagnostics-kind.other'
    ]()
  );
}

function shortId(id: string): string {
  if (id.length <= 16) return id;
  return `${id.slice(0, 8)}...${id.slice(-6)}`;
}
